import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef, useContext } from 'react';
import ReactDOM from 'react-dom';
import cn from 'classnames';
import { childrenShape } from '@bibliocommons/bc-prop-types';
import manageFocus from '@bibliocommons/base-accessibility-manage-focus';
import PortalContext from './PortalContext';

import './Portal.scss';

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

const Portal = manageFocus(props => {
  const portalRef = useRef();
  const prevProps = usePrevious(props);
  const portalContext = useContext(PortalContext);
  const [targetNode, setTargetNode] = useState();
  const { captureFocus, restoreFocus, focusTrapActive } = props;
  const classNames = cn('cp-portal', portalContext.namespace);

  useEffect(() => {
    if (!targetNode) {
      setTargetNode(document.body);
    }

    if (targetNode) {
      if (focusTrapActive && portalRef.current) {
        captureFocus(portalRef.current);
        targetNode.setAttribute('data-focus-trapped', true);

        // Hide the main content from screen readers when focus-trap is activated
        // NOTE: This does not hide other portals that may be on the page.
        Array.from(document.body.children).forEach(node => {
          if (!node.hasAttribute('aria-hidden') && !node.classList.contains('cp-portal')) {
            node.setAttribute('aria-hidden', true);
            node.setAttribute('data-aria-hidden-added', true);
          }
        });
      } else if (prevProps.focusTrapActive) {
        restoreFocus();
        targetNode.removeAttribute('data-focus-trapped');

        // Remove 'aria-hidden' attributes from the main content when focus-trap is deactivated
        Array.from(document.body.children).forEach(node => {
          if (node.hasAttribute('data-aria-hidden-added')) {
            node.removeAttribute('data-aria-hidden-added');
            node.removeAttribute('aria-hidden');
          }
        });
      }
    }
  }, [targetNode, focusTrapActive, restoreFocus, captureFocus, prevProps]);

  if (targetNode) {
    return ReactDOM.createPortal(
      <div className={classNames} ref={portalRef}>
        {props.children}
      </div>,
      targetNode
    );
  }

  return null;
});

Portal.propTypes = {
  children: childrenShape.isRequired,
  focusTrapActive: PropTypes.bool
};

export default Portal;
